import { ResponseError } from '@backstage/errors';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { AdpApi } from './api';
import {
  BillingInformation,
  BillingInformationValidationResponse,
  OrderSummary,
  OrderTenant,
  OwnerConfirmationResult,
  OrderDataProduct,
  Order,
  Tenant,
  OrderStatus,
  BusinessUnit,
} from '@mercedes-benz/backstage-plugin-adp-common';

/**
 * Options for creating a AdpClient client.
 *
 * @public
 */
export interface AdpClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}

/**
 * An implementation of the client
 *
 * @public
 */
export class AdpClient implements AdpApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: AdpClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getTenants(platform?: 'DATABRICKS' | 'AWS'): Promise<Tenant[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('adp');
    let targetUrl = `${baseUrl}/tenants`;

    if (platform) {
      targetUrl = `${targetUrl}?platform=${platform}`;
    }

    const res = await this.fetchApi.fetch(targetUrl);

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    return await res.json();
  }

  async orderTenant(tenantData: OrderTenant): Promise<any> {
    const baseUrl = await this.discoveryApi.getBaseUrl('adp');
    const targetUrl = `${baseUrl}/tenants`;

    const res = await this.fetchApi.fetch(targetUrl, {
      method: 'POST',
      body: JSON.stringify(tenantData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    return await res.json();
  }

  async orderDataProduct(dataProductData: OrderDataProduct): Promise<Order> {
    const baseUrl = await this.discoveryApi.getBaseUrl('adp');
    const targetUrl = `${baseUrl}/dataproducts`;

    const res = await this.fetchApi.fetch(targetUrl, {
      method: 'POST',
      body: JSON.stringify(dataProductData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    return await res.json();
  }

  async getOrderStatus(orderId: string): Promise<OrderStatus> {
    const baseUrl = await this.discoveryApi.getBaseUrl('adp');
    const targetUrl = `${baseUrl}/orders/${orderId}/status`;
    const res = await this.fetchApi.fetch(targetUrl);

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return await res.json();
  }

  async getOrder(orderId: string): Promise<Order> {
    const baseUrl = await this.discoveryApi.getBaseUrl('adp');
    const targetUrl = `${baseUrl}/orders/${orderId}`;
    const res = await this.fetchApi.fetch(targetUrl);

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return await res.json();
  }

  async validateBillingInformation(
    billingInfo: BillingInformation,
  ): Promise<BillingInformationValidationResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('adp');
    const targetUrl = `${baseUrl}/billing/validate`;

    const res = await this.fetchApi.fetch(targetUrl, {
      method: 'POST',
      body: JSON.stringify(billingInfo),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    return await res.json();
  }

  async resolveOwnerConfirmation(
    orderId: string,
    action: 'accept' | 'reject',
    token: string,
  ): Promise<OwnerConfirmationResult> {
    const baseUrl = await this.discoveryApi.getBaseUrl('adp');
    const targetUrl = `${baseUrl}/orders/${orderId}/confirmation`;

    const res = await this.fetchApi.fetch(targetUrl, {
      method: 'POST',
      body: JSON.stringify({ action, token }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    return await res.json();
  }

  async getOrderSummary(orderId: string): Promise<OrderSummary> {
    const baseUrl = await this.discoveryApi.getBaseUrl('adp');
    const targetUrl = `${baseUrl}/orders/${orderId}/summary`;
    const res = await this.fetchApi.fetch(targetUrl);

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return await res.json();
  }

  async resendOwnerConfirmation(
    orderId: string,
    token: string,
  ): Promise<OwnerConfirmationResult> {
    const baseUrl = await this.discoveryApi.getBaseUrl('adp');
    const targetUrl = `${baseUrl}/orders/${orderId}/confirmation/resend`;

    const res = await this.fetchApi.fetch(targetUrl, {
      method: 'POST',
      body: JSON.stringify({ token }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    return await res.json();
  }

  async getBusinessUnits(): Promise<BusinessUnit[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('adp');
    const targetUrl = `${baseUrl}/business-units`;
    const res = await this.fetchApi.fetch(targetUrl);

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return await res.json();
  }
}
